import React, { useEffect } from 'react';

interface AdExclusionManagerProps {
  currentRoute: string;
}

export const AdExclusionManager: React.FC<AdExclusionManagerProps> = ({ currentRoute }) => {
  useEffect(() => {
    // Check if we're on a blog page
    const isBlogPage = currentRoute === 'blog' || currentRoute === 'blog-post' || 
                      window.location.pathname.startsWith('/blog');

    // Add or remove ad exclusion meta tags
    const updateAdExclusion = () => {
      // Remove existing ad exclusion meta tags
      const existingMeta = document.querySelector('meta[name="google-adsense-autoads"]');
      if (existingMeta) {
        existingMeta.remove();
      }

      // Add exclusion meta tag for non-blog pages
      if (!isBlogPage) {
        const meta = document.createElement('meta');
        meta.name = 'google-adsense-autoads';
        meta.content = 'false';
        document.head.appendChild(meta);
      }
    };

    updateAdExclusion();

    const purgeAdSense = () => {
      if (isBlogPage) return;
      try {
        // Remove AdSense scripts
        const scripts = Array.from(document.querySelectorAll('script[src*="pagead2.googlesyndication.com"]')) as HTMLScriptElement[];
        for (const s of scripts) {
          s.parentElement?.removeChild(s);
        }
        // Remove AdSense ad containers
        const adIns = Array.from(document.querySelectorAll('ins.adsbygoogle')) as HTMLElement[];
        for (const el of adIns) {
          el.remove();
        }
        // Remove ad iframes (google_esf and ad iframes)
        const adIframes = Array.from(document.querySelectorAll(
          'iframe#google_esf, iframe[id^="google_ads_iframe"], iframe[src*="googlesyndication"], iframe[src*="doubleclick"]'
        )) as HTMLIFrameElement[];
        for (const ifr of adIframes) {
          ifr.parentElement?.removeChild(ifr);
        }
        // Remove possible containers with id google_esf
        const esfContainers = Array.from(document.querySelectorAll('#google_esf')) as HTMLElement[];
        for (const c of esfContainers) {
          c.remove();
        }
        // Clear global objects
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        try { delete w.adsbygoogle; } catch {}
        try { delete w.google_jobrunner; } catch {}
        try { delete w.google_ad_modifications; } catch {}
        try { delete w.google_pmc; } catch {}
        try { delete w.googletag; } catch {}
      } catch {}
    };

    // Purge immediately and after a tick (in case scripts re-attach during route change)
    purgeAdSense();
    const t = window.setTimeout(purgeAdSense, 0);

    // Observe DOM for any new ad iframes/scripts and remove them on non-blog pages
    const observer = new MutationObserver(() => {
      purgeAdSense();
    });
    if (!isBlogPage) {
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    // Also update the page title to help with ad targeting
    if (!isBlogPage) {
      // Add a class to body to help with CSS-based ad exclusion
      document.body.classList.add('no-ads');
    } else {
      document.body.classList.remove('no-ads');
    }

    // Cleanup function
    return () => {
      const meta = document.querySelector('meta[name="google-adsense-autoads"]');
      if (meta) {
        meta.remove();
      }
      document.body.classList.remove('no-ads');
      // Also purge on unmount when leaving pages using this manager
      purgeAdSense();
      window.clearTimeout(t);
      observer.disconnect();
    };
  }, [currentRoute]);

  return null; // This component doesn't render anything
};

// Hook to check if ads should be excluded
export const useAdExclusion = (): boolean => {
  return !window.location.pathname.startsWith('/blog');
};

// Utility function to check if current page should have ads
export const shouldExcludeAds = (): boolean => {
  const path = window.location.pathname;
  return !path.startsWith('/blog');
};
